import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
} from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import {
  ZodForm,
  FormText,
  FormSwitch,
  FormDropdown,
  FormMultiSelect,
} from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useEventVariables } from '../../../hooks/useEventVariables.js'
import TemplateEditor from '../../../components/admin/conversation-engine/TemplateEditor.js'
import LivePreview from '../../../components/admin/conversation-engine/LivePreview.js'
import Select from '../../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Types — derived from
// apps/api/src/routes/conversations/admin/notification-templates.ts
// (notification_templates table). The POST/PATCH body validates:
//   eventKey   string min(1)            required
//   audience   enum('agent','customer') required
//   channels   array(enum) min(1)       required
//   subjectTemplate string|null         optional
//   bodyTemplate    string min(1)       required
//   isActive   boolean                  optional
//   locale     string min(2)            optional
// ---------------------------------------------------------------------------

type Audience = 'agent' | 'customer'
type Channel = 'email' | 'sms' | 'in_app' | 'push'

export interface NotificationTemplate {
  id: string
  eventKey: string
  audience: Audience
  channels: Channel[]
  subjectTemplate?: string | null
  bodyTemplate: string
  isActive: boolean
  locale?: string
}

const AUDIENCE_OPTIONS: ReadonlyArray<{ label: string; value: Audience }> = [
  { label: 'Agent', value: 'agent' },
  { label: 'Customer', value: 'customer' },
]

const CHANNEL_OPTIONS: ReadonlyArray<{ label: string; value: Channel }> = [
  { label: 'Email', value: 'email' },
  { label: 'SMS', value: 'sms' },
  { label: 'In-app', value: 'in_app' },
  { label: 'Push', value: 'push' },
]

const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  in_app: 'In-app',
  push: 'Push',
}

const AUDIENCE_LABELS: Record<Audience, string> = {
  agent: 'Agent',
  customer: 'Customer',
}

const TemplateSchema = z.object({
  eventKey: z.string().min(1, 'Event key is required'),
  audience: z.enum(['agent', 'customer']),
  channels: z
    .array(z.enum(['email', 'sms', 'in_app', 'push']))
    .min(1, 'Select at least one channel'),
  isActive: z.boolean(),
  locale: z.string().min(2, 'Locale is required'),
})

type TemplateFormValues = z.infer<typeof TemplateSchema>

export default function NotificationTemplatesPage(): JSX.Element {
  const { items, loading, create, update, remove } =
    useAdminResource<NotificationTemplate>(
      '/admin/conversations/notification-templates',
      { responseKey: 'rows' },
    )

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<NotificationTemplate | null>(null)
  // subjectTemplate / bodyTemplate are owned outside the ZodForm — they are
  // driven by TemplateEditor (which can also collapse subject to null when the
  // channel set carries no subject), the same pattern EscalationRulesPage uses
  // for `conditions` / `actions`.
  const [subjectTemplate, setSubjectTemplate] = useState<string | null>('')
  const [bodyTemplate, setBodyTemplate] = useState<string>('')
  const [audienceFilter, setAudienceFilter] = useState<string>('')
  const [channelFilter, setChannelFilter] = useState<string>('')
  const [eventFilter, setEventFilter] = useState<string>('')
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setSubjectTemplate('')
    setBodyTemplate('')
    setDrawerOpen(true)
  }

  function openEdit(row: NotificationTemplate): void {
    setEditing(row)
    setSubjectTemplate(row.subjectTemplate ?? '')
    setBodyTemplate(row.bodyTemplate)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
  }

  async function handleSubmit(data: TemplateFormValues): Promise<void> {
    const payload = {
      eventKey: data.eventKey.trim(),
      audience: data.audience,
      channels: data.channels,
      subjectTemplate: subjectTemplate === '' ? null : subjectTemplate,
      bodyTemplate,
      isActive: data.isActive,
      locale: data.locale.trim(),
    }
    if (editing) {
      await update(editing.id, payload)
    } else {
      await create(payload)
    }
    closeDrawer()
  }

  function handleDelete(row: NotificationTemplate): void {
    confirmDialog({
      message: `Permanently delete "${row.eventKey}" template? Automation rules and escalation actions referencing this template will fail silently. This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  // -- Filtering -------------------------------------------------------------
  const filtered = items.filter((t) => {
    if (audienceFilter && t.audience !== audienceFilter) return false
    if (channelFilter && !t.channels.includes(channelFilter as Channel)) {
      return false
    }
    if (eventFilter && t.eventKey !== eventFilter) return false
    return true
  })

  // Unique event keys for the event filter dropdown.
  const eventKeys = [...new Set(items.map((t) => t.eventKey))].sort()

  const columns: ColumnDef<NotificationTemplate>[] = [
    { label: 'Event Key', accessor: 'eventKey' },
    {
      label: 'Audience',
      accessor: (t) => AUDIENCE_LABELS[t.audience],
    },
    {
      label: 'Channels',
      accessor: (t) => (
        <span className="flex flex-wrap gap-1">
          {t.channels.map((c) => (
            <span
              key={c}
              className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              {CHANNEL_LABELS[c]}
            </span>
          ))}
        </span>
      ),
    },
    {
      label: 'Subject',
      accessor: (t) => t.subjectTemplate ?? '—',
    },
    { label: 'Locale', accessor: (t) => t.locale ?? 'en' },
    {
      label: 'Active',
      accessor: (t) => (
        <InputSwitch
          checked={t.isActive}
          onChange={(e) => void update(t.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
  ]

  const defaultValues: TemplateFormValues = editing
    ? {
        eventKey: editing.eventKey,
        audience: editing.audience,
        channels: [...editing.channels],
        isActive: editing.isActive,
        locale: editing.locale ?? 'en',
      }
    : {
        eventKey: '',
        audience: 'agent',
        channels: ['in_app'],
        isActive: true,
        locale: 'en',
      }

  return (
    <div className="p-4">
      <PageHeader
        title="Notification Templates"
        description="Per-event notification copy for agents and customers, across email, SMS, in-app and push."
        actions={<Button label="+ New Template" onClick={openCreate} />}
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Filter by audience</span>
                    <Select
            ariaLabel="Filter by audience"
            size="sm"
            value={audienceFilter}
            onChange={(v) => setAudienceFilter(v)}
          >
            <option value="">All audiences</option>
            {AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
                    </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Filter by channel</span>
                    <Select
            ariaLabel="Filter by channel"
            size="sm"
            value={channelFilter}
            onChange={(v) => setChannelFilter(v)}
          >
            <option value="">All channels</option>
            {CHANNEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
                    </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Filter by event</span>
                    <Select
            ariaLabel="Filter by event"
            size="sm"
            value={eventFilter}
            onChange={(v) => setEventFilter(v)}
          >
            <option value="">All events</option>
            {eventKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
                    </Select>
        </label>
      </div>

      <ResourceTable<NotificationTemplate>
        columns={columns}
        rows={filtered}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        empty={{
          title: 'No notification templates',
          description:
            'Create a template to define notification copy for an event.',
          action: <Button label="+ New Template" onClick={openCreate} />,
        }}
      />

      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        width="wide"
        title={
          editing
            ? `Edit Template: ${editing.eventKey}`
            : 'New Notification Template'
        }
      >
        <ZodForm
          schema={TemplateSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          render={(methods) => {
            const watchedEventKey = methods.watch('eventKey')
            const watchedAudience = methods.watch('audience') as Audience
            const watchedChannels = (methods.watch('channels') ??
              []) as Channel[]
            return (
              <>
                <FormText name="eventKey" label="Event Key" required autoFocus />
                <FormDropdown
                  name="audience"
                  label="Audience"
                  options={[...AUDIENCE_OPTIONS]}
                  required
                />
                <FormMultiSelect
                  name="channels"
                  label="Channels"
                  options={[...CHANNEL_OPTIONS]}
                  required
                />
                <FormSwitch name="isActive" label="Active" />
                <FormText name="locale" label="Locale" />

                {/* TemplateEditor + LivePreview side-by-side. */}
                <div className="mt-4 flex flex-col gap-4 lg:flex-row">
                  <div className="flex-1">
                    <TemplateEditor
                      subjectTemplate={subjectTemplate}
                      bodyTemplate={bodyTemplate}
                      channels={watchedChannels}
                      audience={watchedAudience}
                      eventKey={watchedEventKey}
                      onChange={(next) => {
                        setSubjectTemplate(next.subjectTemplate)
                        setBodyTemplate(next.bodyTemplate)
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <TemplatePreviewPane
                      subjectTemplate={subjectTemplate}
                      bodyTemplate={bodyTemplate}
                      channels={watchedChannels}
                      eventKey={watchedEventKey}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="mt-4 rounded bg-[#16273F] px-4 py-2 text-white"
                >
                  Save
                </button>
              </>
            )
          }}
        />
      </ResourceDrawer>
    </div>
  )
}

/**
 * LivePreview wrapper that resolves the event-variable schema via
 * {@link useEventVariables}. Kept as a small component so the hook is called
 * unconditionally with the form-watched `eventKey`.
 */
function TemplatePreviewPane({
  subjectTemplate,
  bodyTemplate,
  channels,
  eventKey,
}: {
  subjectTemplate: string | null
  bodyTemplate: string
  channels: Channel[]
  eventKey: string
}): JSX.Element {
  const { variables } = useEventVariables(eventKey)
  return (
    <LivePreview
      subjectTemplate={subjectTemplate}
      bodyTemplate={bodyTemplate}
      channels={channels}
      variables={variables}
    />
  )
}

// Re-export for tests / external consumers.
export { AUDIENCE_OPTIONS, CHANNEL_OPTIONS }
